import { Store } from 'vuex';
import { Context, Plugin } from '@nuxt/types';
import Applicant from '~/models/Applicant';
import Employee from '~/models/Employee';

declare module 'vue/types/vue' {
  interface Vue {
    $auth: Auth;
  }
}

declare module '@nuxt/types' {
  interface NuxtAppOptions {
    $auth: Auth;
  }

  interface Context {
    $auth: Auth;
  }
}

class Auth {
  $store: Store<any>;
  storeKey: string;

  constructor(store: Store<any>) {
    this.$store = store;
    this.storeKey = 'auth';
  }

  get loggedIn() {
    return this.state('loggedIn');
  }

  get me() {
    return this.state('me');
  }

  get user() {
    return this.getter('user');
  }

  get userType() {
    return this.getter('userType');
  }

  get isEmployee() {
    return this.getter('isEmployee');
  }

  get isApplicant() {
    return this.getter('isApplicant');
  }

  get isTenantMember() {
    return this.getter('isTenantMember');
  }

  get isAdmin() {
    return this.getter('isAdmin');
  }

  get isLoggedIn() {
    return this.getter('isLoggedIn');
  }

  get isGuest() {
    return this.getter('isGuest');
  }

  get isVerified() {
    return this.getter('isVerified');
  }

  login(payload: any) {
    return this.dispatch('login', payload);
  }

  register(payload: any) {
    return this.dispatch('register', payload);
  }

  logout() {
    return this.dispatch('logout');
  }

  forgotPassword(payload: any) {
    return this.dispatch('forgotPassword', payload);
  }

  resetPassword(payload: any) {
    return this.dispatch('resetPassword', payload);
  }

  changePassword(payload: any) {
    return this.dispatch('changePassword', payload);
  }

  fetchMe(payload: any) {
    return this.dispatch('fetchMe', payload);
  }

  updateUser(payload: any) {
    return this.dispatch('updateUser', payload);
  }

  state(key: string) {
    return this.$store.state[this.storeKey][key];
  }

  getter(key: string) {
    return this.$store.getters[`${this.storeKey}/${key}`];
  }

  commit(key: string, payload: any) {
    return this.$store.commit(`${this.storeKey}/${key}`, payload);
  }

  dispatch(method: string, payload?: any) {
    return this.$store.dispatch(`${this.storeKey}/${method}`, payload);
  }

  hasOnboarded({ supportsRelocationFeature }: { supportsRelocationFeature?: boolean }) {
    if (this.isApplicant) {
      return new Applicant(this.me).hasOnboarded({ supportsRelocationFeature });
    }

    if (this.isEmployee) {
      return new Employee(this.me).hasOnboarded();
    }

    return true;
  }

  getOnboardingRoute() {
    if (this.isApplicant) {
      return new Applicant(this.me).getOnboardingRoute();
    }

    if (this.isEmployee) {
      return new Employee(this.me).getOnboardingRoute();
    }

    return '';
  }
}

const authPlugin: Plugin = ({ store }: Context, inject) => {
  inject('auth', new Auth(store));
};

export default authPlugin;
